import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ComponentProps, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { flattenOptions } from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import { OptionType } from 'dashboard/components/ReactSelect';
import {
  FilterTagsCondition,
  SegmentedTagsConditionFilter,
} from 'dashboard/components/TagsConditionFilter';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { Tag } from 'dashboard/models/tag';
import { Team } from 'dashboard/models/team';
import { User, UserStatus } from 'dashboard/models/user';
import { CasesFilterParams, FilterValues } from 'dashboard/redux/actions/cases';
import { RootState } from 'dashboard/redux/store';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';

export interface CasesTableControlsProps {
  activeCaseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  filters: CasesFilterParams;
  loadingCaseTemplateBasicsOptions: boolean;
  caseQueues: CaseQueue[];
  tags: Tag[];
  activeTeams: Team[];
  users: User[];
  handleAssigneeFilterUpdate: (
    userAssigneeIds: FilterValues,
    teamAssigneeIds: FilterValues,
  ) => void;
  handleNotAssigneeFilterUpdate: (userAssigneeIds: string[]) => void;
  handleCaseTemplateFilterUpdate: (templateIds: FilterValues) => void;
  handleCaseQueueFilterUpdate: (caseQueueIds: FilterValues) => void;
  handleDateTypeFilterUpdate: (dateType: 'created_at' | 'resolved_at') => void;
  handleDateUpdate: (
    range: RangeValue<DateValue> | null,
    dateType?: 'created_at' | 'resolved_at',
  ) => void;
  handleStateFilterUpdate: (stateIds: FilterValues) => void;
  handleTagsConditionsFilterUpdate: (tagsConditions: FilterTagsCondition[][]) => void;
  handleOmniSearch: ComponentProps<typeof TableControls>['searchProps']['handleInput'];
  searchNextCasesBackendEnabled: boolean;
  tableActions: ReactNode;
  propertiesButton: ReactNode;
}

const CasesTableControls = ({
  activeCaseTemplateBasicsOptions,
  filters,
  loadingCaseTemplateBasicsOptions,
  tags,
  activeTeams,
  caseQueues,
  users,
  handleAssigneeFilterUpdate,
  handleNotAssigneeFilterUpdate,
  handleCaseTemplateFilterUpdate,
  handleCaseQueueFilterUpdate,
  handleDateUpdate,
  handleDateTypeFilterUpdate,
  handleStateFilterUpdate,
  handleTagsConditionsFilterUpdate,
  handleOmniSearch,
  searchNextCasesBackendEnabled,
  tableActions,
  propertiesButton,
}: CasesTableControlsProps) => {
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);

  const caseTemplateDropdownOptions = useMemo(
    () =>
      activeCaseTemplateBasicsOptions.map((option) => ({
        label: `${option.name} (${option['case-prefix']})`,
        value: option.id,
      })),
    [activeCaseTemplateBasicsOptions],
  );

  const stateFilterOptions = useMemo(() => {
    const stateNames = uniq(
      activeCaseTemplateBasicsOptions
        .flatMap((option) => option.states)
        .flatMap((state) => state.name),
    );

    const options = stateNames.sort().map((stateName: string) => ({
      label: stateName,
      value: stateName,
    }));

    return options;
  }, [activeCaseTemplateBasicsOptions]);

  const caseQueueOptions = useMemo(() => {
    const none = [
      {
        label: 'No queue',
        value: 'none',
      },
    ];

    const queues = caseQueues
      .filter((queue) => queue.attributes.status === 'active')
      .map((queue) => ({
        label: queue.attributes.name,
        value: queue.id,
      }))
      .sort((o1, o2) => o1.label.localeCompare(o2.label));

    return [...none, ...queues];
  }, [caseQueues]);

  const userFilterOptions = useMemo(() => {
    const unassigned = [
      {
        label: 'Unassigned',
        value: 'unassigned',
      },
    ];

    const myUser = users.some((user: User) => user.id === currentUser.id)
      ? [
          {
            label: `${userDisplayName(currentUser)} (Assigned to me)`,
            value: currentUser.id,
          },
        ]
      : [];

    const otherUsers = users
      .filter((user) => user.attributes.status === UserStatus.Active && user.id !== currentUser.id)
      .map((user) => ({
        label: userDisplayName(user),
        value: user.id,
      }))
      .filter((userOption) => !(userOption.label == null))
      .sort((o1, o2) => (o1.label as string).localeCompare(o2.label as string));

    return [...unassigned, ...myUser, ...otherUsers];
  }, [currentUser, users]);

  const teamFilterOptions = useMemo(
    () =>
      activeTeams.map((team) => {
        return {
          label: team.attributes.name,
          value: team.id,
        };
      }),
    [activeTeams],
  );

  const assigneeFilterOptions: OptionType<string | undefined>[] = [
    {
      label: 'Teams',
      value: undefined,
      options: teamFilterOptions,
    },
    {
      label: 'Users',
      value: undefined,
      options: userFilterOptions,
    },
  ];

  const tagFilterOptions = useMemo(
    () =>
      tags.map((tag) => ({
        label: tag.attributes.name,
        value: tag.id,
      })),
    [tags],
  );

  const onCaseTemplateFilterChange = (caseTemplateIds: string[], isSelectAll: boolean) => {
    if (isSelectAll) {
      handleCaseTemplateFilterUpdate('all');
    } else {
      handleCaseTemplateFilterUpdate(caseTemplateIds);
    }
  };

  const onStatusFilterChange = (caseStateNames: string[], isSelectAll: boolean) => {
    if (isSelectAll) {
      handleStateFilterUpdate('all');
    } else {
      handleStateFilterUpdate(caseStateNames);
    }
  };

  const onAssigneeFilterChange = (assigneeIds: string[], isSelectAll: boolean) => {
    if (isSelectAll) {
      handleAssigneeFilterUpdate('all', 'all');
    } else {
      const teamIds = assigneeIds.filter((resolverId) => resolverId.startsWith('team_'));
      const userIds = assigneeIds.filter((resolverId) => !resolverId.startsWith('team_'));
      handleAssigneeFilterUpdate(userIds, teamIds);
    }
  };

  const onNotAssigneeFilterChange = (assigneeIds: string[]) => {
    const userIds = assigneeIds.filter((resolverId) => !resolverId.startsWith('team_'));
    handleNotAssigneeFilterUpdate(userIds);
  };

  const onCaseQueueFilterChange = (caseQueueIds: string[], isSelectAll: boolean) => {
    if (isSelectAll) {
      handleCaseQueueFilterUpdate('all');
    } else {
      handleCaseQueueFilterUpdate(caseQueueIds);
    }
  };

  const filterConfig: FilterConfig[] = [
    {
      id: 'createdAt',
      name: `Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
      type: 'daterange',
      group: 'date',
      onChange: (date) => {
        handleDateUpdate(date, 'created_at');
      },
      onFilterSelect: () => {
        if (filters.dateType === 'resolved_at') {
          handleDateTypeFilterUpdate('created_at');
        }
      },
      value:
        filters.dateType === 'created_at'
          ? {
              startDate: filters.startDate,
              endDate: filters.endDate,
            }
          : { startDate: undefined, endDate: undefined },
    },
    {
      id: 'resolvedAt',
      name: `Resolved at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
      type: 'daterange',
      group: 'date',
      onChange: (date) => {
        handleDateUpdate(date, 'resolved_at');
      },
      onFilterSelect: () => {
        if (filters.dateType === 'created_at') {
          handleDateTypeFilterUpdate('resolved_at');
        }
      },
      value:
        filters.dateType === 'resolved_at'
          ? {
              startDate: filters.startDate,
              endDate: filters.endDate,
            }
          : { startDate: undefined, endDate: undefined },
    },
    {
      id: 'caseTemplate',
      name: 'Case template',
      type: 'multiselect-alt',
      placeholderGenerator: generatePlaceHolderDisplay({
        label: { single: 'Case Template', multiple: 'Case Templates' },
        emptyIsAll: true,
      }),
      options: caseTemplateDropdownOptions,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onChange: onCaseTemplateFilterChange,
      selectAllEnabled: true,
      searchEnabled: true,
      value: generateFilterValues(filters.caseTemplateIds || [], caseTemplateDropdownOptions),
    },
    {
      id: 'status',
      name: 'Status',
      type: 'multiselect-alt',
      placeholderGenerator: generatePlaceHolderDisplay({
        label: { single: 'Status', multiple: 'Statuses' },
        emptyIsAll: true,
      }),
      options: stateFilterOptions,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onChange: onStatusFilterChange,
      selectAllEnabled: true,
      searchEnabled: true,
      value: generateFilterValues(filters.caseStateNames || [], stateFilterOptions),
    },
    {
      id: 'assignee',
      name: 'Assigned to',
      type: 'multiselect-alt',
      group: 'assignees',
      placeholderGenerator: generatePlaceHolderDisplay({
        label: { single: 'Assignee', multiple: 'Assignees' },
        emptyIsAll: true,
      }),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      options: assigneeFilterOptions,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onChange: onAssigneeFilterChange,
      selectAllEnabled: true,
      searchEnabled: true,
      value:
        filters.teamAssigneeIds === 'all'
          ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            flattenOptions(assigneeFilterOptions).map((option) => option.value)
          : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            [...filters.teamAssigneeIds, ...filters.assigneeIds],
    },
    {
      id: 'notAssignee',
      name: 'Not assigned to',
      type: 'multiselect-alt',
      group: 'assignees',
      placeholderGenerator: generatePlaceHolderDisplay({
        label: { single: 'Assignee', multiple: 'Assignees' },
        emptyIsAll: false,
      }),
      options: userFilterOptions.filter((userOption) => userOption.value !== 'unassigned'),
      onChange: onNotAssigneeFilterChange,
      selectAllEnabled: false,
      searchEnabled: true,
      value: filters.notAssigneeIds || [],
    },
    {
      id: 'caseQueue',
      name: 'Case queue',
      type: 'multiselect-alt',
      enabled: caseQueues.length > 0,
      placeholderGenerator: generatePlaceHolderDisplay({
        label: { single: 'Case Queue', multiple: 'Case Queues' },
        emptyIsAll: true,
      }),
      options: caseQueueOptions,
      selectAllEnabled: true,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onChange: onCaseQueueFilterChange,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value:
        filters.caseQueueIds === 'all'
          ? flattenOptions(caseQueueOptions).map((option) => option.value)
          : filters.caseQueueIds,
    },
    {
      id: 'tags',
      name: 'Tags',
      type: 'custom',
      enabled: tags.length > 0,
      filterElement: (
        <SegmentedTagsConditionFilter
          ariaLabelledBy='case-table-savable-filters__tags-label'
          values={filters.tagsConditions}
          tagOptions={tagFilterOptions}
          onChange={(tagsConditions: FilterTagsCondition[][]) => {
            handleTagsConditionsFilterUpdate(tagsConditions);
          }}
          productName='case'
        />
      ),
      onDelete: () => handleTagsConditionsFilterUpdate([]),
      hasValue: filters.tagsConditions && filters.tagsConditions.length > 0,
    },
  ];

  return (
    <TableControls
      searchProps={{
        id: 'configurable-cases-table__search',
        handleInput: handleOmniSearch,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        value: filters.omni,
        placeholder: searchNextCasesBackendEnabled
          ? 'Search by text'
          : 'Search by case ID, reference ID, account ID, or inquiry ID',
      }}
      filters={filterConfig}
      tableActions={tableActions}
      propertiesButton={propertiesButton}
      isLoading={loadingCaseTemplateBasicsOptions}
    />
  );
};

const generateFilterValues = (filterValues: FilterValues, options: OptionType[]): string[] => {
  return filterValues === 'all'
    ? flattenOptions(options).map((option) => option.value)
    : filterValues;
};

export default CasesTableControls;
